import { memo, useState } from 'react';
import FilterDropdown, { type FilterSection } from './FilterDropdown';

/**
 * SearchInputProps interface for the isolated SearchInput component
 *
 * @interface SearchInputProps
 * @property {(value: string) => void} [onSearchChange] - Optional callback fired when search value changes
 * @property {string} [searchPlaceholder='Search'] - Optional placeholder text for the search input
 */
interface SearchInputProps {
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
}

const SearchInput = memo(function SearchInput({
  onSearchChange,
  searchPlaceholder = 'Search',
}: SearchInputProps) {
  const [searchValue, setSearchValue] = useState('');

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    onSearchChange?.(value);
  };

  return (
    <div className="flex w-full items-center gap-2 rounded-[0.75rem] border-1 border-[#f1f1f1] bg-[#f9f9f9] px-3 py-2 md:w-[16.5rem]">
      <div className="flex h-5 w-5 shrink-0 items-center justify-center">
        <img src="/assets/icons/search.svg" alt="Search" className="h-full w-full object-contain" />
      </div>
      <input
        type="text"
        value={searchValue}
        onChange={handleSearchChange}
        placeholder={searchPlaceholder}
        className="flex flex-shrink-0 flex-grow basis-0 items-center border-none bg-transparent leading-6 text-black outline-none placeholder:text-[#666]"
      />
    </div>
  );
});

/**
 * TableHeaderProps interface defining the props for the TableHeader component
 *
 * @interface TableHeaderProps
 * @property {string} title - The main title displayed in the header (required)
 * @property {() => void} [onFilterClick] - Optional callback fired when filter button is clicked
 * @property {(value: string) => void} [onSearchChange] - Optional callback fired when search value changes
 * @property {string} [searchPlaceholder='Search'] - Optional placeholder text for the search input
 */
interface TableHeaderProps {
  /** The main title displayed prominently in the header */
  title: string;
  /** Filter sections for the dropdown */
  filterSections?: FilterSection[];
  /** Callback function executed when filters change */
  onFiltersChange?: (filters: Record<string, string[]>) => void;
  /** Callback function executed when search input value changes */
  onSearchChange?: (value: string) => void;
  /** Placeholder text shown in the search input field */
  searchPlaceholder?: string;
}

/**
 * TableHeader - Main header component for data tables with filtering and search capabilities
 *
 * **Performance Features:**
 * - Uses React.memo to prevent unnecessary re-renders when props haven't changed
 * - Isolates SearchInput component to prevent header re-renders during typing
 * - Optimized for tables with frequent user interactions
 *
 * **Component Architecture:**
 * - Left section: Main title display
 * - Right section: Filter button and search input in a responsive container
 * - Uses custom Button component with unstyled variant for maximum flexibility
 *
 * **Design System Integration:**
 * - Follows Cellfie design tokens for spacing, colors, and typography
 * - Uses rem-based measurements for responsive design
 * - Integrates with custom icon system via SVG imports
 *
 * @component
 * @example
 * ```tsx
 * // Basic usage with title only
 * <TableHeader title="Business Trips" />
 *
 * // Full featured with search and filter
 * <TableHeader
 *   title="Trip Management"
 *   onFilterClick={() => setFilterOpen(true)}
 *   onSearchChange={(value) => setSearchQuery(value)}
 *   searchPlaceholder="Search trips..."
 * />
 *
 * // With Georgian text (default)
 * <TableHeader
 *   title="Business Trips"
 *   onSearchChange={handleSearch}
 *   // Uses default placeholder "Search"
 * />
 * ```
 *
 * @param {TableHeaderProps} props - Component props
 * @param {string} props.title - The main title displayed in the header
 * @param {() => void} [props.onFilterClick] - Optional callback for filter button clicks
 * @param {(value: string) => void} [props.onSearchChange] - Optional callback for search value changes
 * @param {string} [props.searchPlaceholder='Search'] - Optional search input placeholder
 *
 * @returns {JSX.Element} Rendered table header component
 *
 * @since 1.0.0
 * @see {@link SearchInput} for isolated search component details
 * @see {@link Button} for custom button component with variants
 *
 * @performance
 * - Memoized to prevent re-renders when parent state changes
 * - SearchInput isolation prevents keystroke-triggered re-renders
 * - Efficient for high-frequency user interaction scenarios
 */
const TableHeader = memo(function TableHeader({
  title,
  filterSections,
  onFiltersChange,
  onSearchChange,
  searchPlaceholder = 'Search',
}: TableHeaderProps) {
  return (
    <div className="flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between md:gap-0">
      {/* Title */}
      <h1 className="text-[1.5rem] leading-[2rem] font-medium text-[#292929] md:text-[2rem] md:leading-[2.5rem]">
        {title}
      </h1>

      {/* Filter and Search Container */}
      <div className="flex w-full items-center gap-3 md:w-[23.75rem] md:gap-4">
        {/* Filter Button */}
        <div className="flex-1 md:flex-initial">
          <FilterDropdown
            sections={filterSections || []}
            onFiltersChange={onFiltersChange}
            className="w-full md:w-auto"
          />
        </div>

        {/* Search Input */}
        <div className="flex-1 md:flex-initial">
          <SearchInput onSearchChange={onSearchChange} searchPlaceholder={searchPlaceholder} />
        </div>
      </div>
    </div>
  );
});

export default TableHeader;
